import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Shield, Search, Users, ChevronRight, Save, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalAccessManager({ token }) {
  const [employees, setEmployees] = useState([]);
  const [portals, setPortals] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [accessMap, setAccessMap] = useState({});
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  const config = { headers: { Authorization: `Bearer ${token}` } };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, portalRes] = await Promise.all([
        fetch(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/portal-registry`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const empData = await empRes.json();
      const portalData = await portalRes.json();
      setEmployees(Array.isArray(empData) ? empData : []);
      setPortals(portalData.portals || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAccessDialog = async (employee) => {
    setSelectedEmployee(employee);
    try {
      const res = await fetch(`${API}/portal-access/${employee.employee_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAccessMap(data.portals || {});
    } catch {
      // Default all enabled
      const defaults = {};
      portals.forEach(p => { defaults[p.key] = true; });
      setAccessMap(defaults);
    }
    setShowDialog(true);
  };

  const togglePortal = (key) => {
    setAccessMap(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleAll = (enabled) => {
    const updated = {};
    portals.forEach(p => { updated[p.key] = enabled; });
    setAccessMap(updated);
  };

  const saveAccess = async () => {
    if (!selectedEmployee) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/portal-access/${selectedEmployee.employee_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ portals: accessMap })
      });
      if (res.ok) {
        toast.success(`Portal access updated for ${selectedEmployee.name}`);
        setShowDialog(false);
      } else {
        toast.error('Failed to update access');
      }
    } catch {
      toast.error('Failed to update access');
    } finally {
      setSaving(false);
    }
  };

  const filtered = employees.filter(e =>
    e.role !== 'superadmin' &&
    (e.name?.toLowerCase().includes(search.toLowerCase()) ||
     e.employee_id?.toLowerCase().includes(search.toLowerCase()) ||
     e.department?.toLowerCase().includes(search.toLowerCase()))
  );

  const enabledCount = Object.values(accessMap).filter(Boolean).length;
  const categoryGroups = portals.reduce((acc, p) => {
    const cat = p.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  const categoryLabels = {
    employee: 'Employee Portals',
    manager: 'Manager Portals',
    hr: 'HR Portals',
    admin: 'Admin Portals',
    other: 'Other'
  };

  return (
    <div className="space-y-4" data-testid="portal-access-manager">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" /> Portal Access Management
              </CardTitle>
              <CardDescription>Enable or disable portal access for each employee</CardDescription>
            </div>
            <Badge variant="outline">{portals.length} Portals Registered</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 mb-4">
            <Search className="h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name, ID, or department..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-sm"
              data-testid="search-employees"
            />
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading employees...</div>
          ) : (
            <div className="rounded-md border">
              <Table data-testid="portal-access-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24">Emp ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((emp) => (
                    <TableRow key={emp.id} data-testid={`emp-row-${emp.employee_id}`}>
                      <TableCell className="font-mono text-sm">{emp.employee_id}</TableCell>
                      <TableCell className="font-medium">{emp.name}</TableCell>
                      <TableCell>{emp.department || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{emp.role}</Badge>
                      </TableCell>
                      <TableCell>
                        {emp.is_active !== false ? (
                          <Badge className="bg-green-100 text-green-700">Active</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openAccessDialog(emp)}
                          data-testid={`manage-access-${emp.employee_id}`}
                        >
                          <Shield className="h-3.5 w-3.5 mr-1" /> Manage Access
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        No employees found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Portal Access Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="portal-access-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Portal Access - {selectedEmployee?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedEmployee?.employee_id} | {selectedEmployee?.department || 'No Department'} | {enabledCount}/{portals.length} portals enabled
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1 mb-4">
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => toggleAll(true)} data-testid="btn-enable-all">
                Enable All
              </Button>
              <Button size="sm" variant="outline" onClick={() => toggleAll(false)} data-testid="btn-disable-all">
                Disable All
              </Button>
            </div>
          </div>

          <div className="space-y-5">
            {Object.entries(categoryGroups).map(([cat, catPortals]) => (
              <div key={cat}>
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {categoryLabels[cat] || cat}
                </h4>
                <div className="space-y-1">
                  {catPortals.map((portal) => (
                    <div
                      key={portal.key}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                        accessMap[portal.key] ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                      }`}
                      data-testid={`portal-row-${portal.key}`}
                    >
                      <div>
                        <p className="font-medium text-sm">{portal.label}</p>
                        <p className="text-xs text-gray-500">{portal.description}</p>
                      </div>
                      <Switch
                        checked={!!accessMap[portal.key]}
                        onCheckedChange={() => togglePortal(portal.key)}
                        data-testid={`switch-${portal.key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={saveAccess} disabled={saving} data-testid="btn-save-access">
              <Save className="h-4 w-4 mr-1" />
              {saving ? 'Saving...' : 'Save Access'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
